import { runGpuBenchmarkInWorker } from './benchmark/run-gpu-benchmark'
import { resolveAppleDevice } from './device/resolve-apple-device'
import { readPerfLevelOverride } from './device/read-url-overrides'
import { detectSoftwareRenderer, isLegacyWindows } from './guards/software-renderer'
import {
  capPerfProbeLevel,
  resolvePerfProbeLevel,
} from './policy/resolve-perf-probe-level'
import {
  PERF_PROBE_LEVEL,
  type PerfProbeLevel,
  type PerfProbeReason,
  type PerfProbeReport,
  type RunPerfProbeOptions,
} from './types'

export const PERF_PROBE_VERSION = '3'
const CACHE_KEY = `holoview.perf-probe.${PERF_PROBE_VERSION}`
const MINIMUM_MEMORY_GB = 6

let cachedReport: PerfProbeReport | null = null

interface NavigatorWithDeviceMemory extends Navigator {
  readonly deviceMemory?: number
}

export function getCachedPerfProbeReport(): PerfProbeReport | null {
  return cachedReport ?? readPersistedReport()
}

export async function runPerfProbe(
  options: RunPerfProbeOptions = {},
): Promise<PerfProbeReport> {
  if (!options.force) {
    const cached = getCachedPerfProbeReport()
    if (cached) {
      cachedReport = cached
      return cached
    }
  }

  const startedAt = performance.now()
  const apple = resolveAppleDevice()
  const deviceMemoryGb = readDeviceMemoryGb(apple?.ramGb ?? null)
  const override = readDevelopmentOverride()
  if (override) {
    return persist(createReport({
      level: override,
      frames: framesForLevel(override),
      elapsedMs: performance.now() - startedAt,
      deviceMemoryGb,
      reason: 'benchmark',
    }))
  }
  if (deviceMemoryGb !== null && deviceMemoryGb < MINIMUM_MEMORY_GB) {
    return persist(createLowReport('memory-below-6gb', startedAt, deviceMemoryGb))
  }
  if (isLegacyWindows()) {
    return persist(createLowReport('legacy-windows', startedAt, deviceMemoryGb))
  }

  const renderer = detectSoftwareRenderer()
  if (renderer.isSoftware) {
    return persist(createLowReport(
      renderer.renderer === 'no-document' ? 'worker-unavailable' : 'software-renderer',
      startedAt,
      deviceMemoryGb,
      renderer.renderer,
    ))
  }

  let benchmark: Awaited<ReturnType<typeof runGpuBenchmarkInWorker>>
  try {
    benchmark = await runGpuBenchmarkInWorker()
  } catch {
    return persist(createLowReport('worker-unavailable', startedAt, deviceMemoryGb, renderer.renderer))
  }
  if (benchmark.error) {
    return persist(createReport({
      level: PERF_PROBE_LEVEL.Low,
      frames: benchmark.frames,
      elapsedMs: benchmark.elapsedMs || performance.now() - startedAt,
      renderer: renderer.renderer || undefined,
      deviceMemoryGb,
      reason: benchmark.error,
    }))
  }

  const benchmarkLevel = resolvePerfProbeLevel(benchmark.frames)
  const appleCap = resolveAppleCap(apple, benchmarkLevel)
  return persist(createReport({
    level: appleCap.level,
    frames: benchmark.frames,
    elapsedMs: benchmark.elapsedMs,
    renderer: renderer.renderer || undefined,
    deviceMemoryGb,
    reason: appleCap.capped ? 'apple-device-cap' : 'benchmark',
  }))
}

export function resetPerfProbeCache(): void {
  cachedReport = null
  try {
    globalThis.localStorage?.removeItem(CACHE_KEY)
  } catch {
    // Storage can be unavailable in private or restricted browser contexts.
  }
}

function readDeviceMemoryGb(appleMemory: number | null): number | null {
  const nav = typeof navigator === 'undefined'
    ? null
    : navigator as NavigatorWithDeviceMemory
  const value = nav?.deviceMemory ?? appleMemory
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : null
}

function readDevelopmentOverride(): PerfProbeLevel | null {
  const env = (import.meta as ImportMeta & { env?: { readonly DEV?: boolean } }).env
  if (env?.DEV !== true) return null
  const value = readPerfLevelOverride()
  return value === PERF_PROBE_LEVEL.High
    || value === PERF_PROBE_LEVEL.Medium
    || value === PERF_PROBE_LEVEL.Low
    ? value
    : null
}

function resolveAppleCap(
  apple: ReturnType<typeof resolveAppleDevice>,
  level: PerfProbeLevel,
): { level: PerfProbeLevel; capped: boolean } {
  let maximum = apple?.maxLevel
  if (apple && !apple.matched && isIphone()) maximum = PERF_PROBE_LEVEL.Medium
  if (!maximum) return { level, capped: false }
  const capped = capPerfProbeLevel(level, maximum)
  return { level: capped, capped: capped !== level }
}

function isIphone(): boolean {
  return typeof navigator !== 'undefined' && /iPhone|iPod/.test(navigator.userAgent)
}

function createLowReport(
  reason: PerfProbeReason,
  startedAt: number,
  deviceMemoryGb: number | null,
  renderer?: string,
): PerfProbeReport {
  return createReport({
    level: PERF_PROBE_LEVEL.Low,
    frames: 0,
    elapsedMs: performance.now() - startedAt,
    renderer,
    deviceMemoryGb,
    reason,
  })
}

function createReport(input: Omit<PerfProbeReport, 'probeVersion' | 'measuredAt'>): PerfProbeReport {
  return Object.freeze({
    probeVersion: PERF_PROBE_VERSION,
    measuredAt: Date.now(),
    ...input,
  })
}

function persist(report: PerfProbeReport): PerfProbeReport {
  cachedReport = report
  try {
    globalThis.localStorage?.setItem(CACHE_KEY, JSON.stringify(report))
  } catch {
    // The in-memory result remains valid when persistent storage is unavailable.
  }
  return report
}

function readPersistedReport(): PerfProbeReport | null {
  try {
    const raw = globalThis.localStorage?.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PerfProbeReport>
    if (
      parsed.probeVersion !== PERF_PROBE_VERSION
      || !isLevel(parsed.level)
      || typeof parsed.frames !== 'number'
      || typeof parsed.elapsedMs !== 'number'
      || typeof parsed.measuredAt !== 'number'
      || typeof parsed.reason !== 'string'
    ) return null
    return parsed as PerfProbeReport
  } catch {
    return null
  }
}

function isLevel(value: unknown): value is PerfProbeLevel {
  return value === PERF_PROBE_LEVEL.High
    || value === PERF_PROBE_LEVEL.Medium
    || value === PERF_PROBE_LEVEL.Low
}

function framesForLevel(level: PerfProbeLevel): number {
  if (level === PERF_PROBE_LEVEL.High) return 500
  if (level === PERF_PROBE_LEVEL.Medium) return 50
  return 0
}
